import React, { useCallback, useState } from 'react';
import { Badge } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { useDropzone } from 'react-dropzone';
import Loader from '../../../../curation/components/Loader';
import { OnValidateItem } from './typedef';

type Props = {
  onUpload: (file: Blob) => void;
  onValidate?: OnValidateItem;
  /** When true, action buttons use type="button" so they don't submit a parent <Form>. */
  preventSubmit?: boolean;
  /** When true, show the selected file name/size before upload. */
  showSelectedFile?: boolean;
  /** When true, render a slimmer single-line dropzone and small buttons. */
  compact?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export default function DragUpload({
  onUpload,
  onValidate,
  preventSubmit = false,
  showSelectedFile = false,
  compact = false,
}: Props) {
  const buttonType = preventSubmit ? 'button' : undefined;
  const buttonSize = compact ? 'sm' : undefined;
  const [selectedFile, setSelectedFile] = useState<Blob | null>(null);
  const [validationState, setValidationState] = useState<'INIT' | 'VALIDATING' | 'SUCCESS' | 'ERROR'>('INIT'); // INIT | VALIDATING | SUCCESS | ERROR
  const [validationErrors, setValidatationErrors] = useState<string[]>([]);

  const onDrop = useCallback((acceptedFiles) => {
    setValidationState('INIT');
    setValidatationErrors([]);
    if (acceptedFiles) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
    }
  }, []);

  // @ts-ignore
  const handleUpload = () => onUpload(selectedFile);
  const handleValidate = async () => {
    if (!onValidate) return;
    setValidationState('VALIDATING');
    // @ts-ignore
    const errors = await onValidate(selectedFile);
    if (errors && errors.length) {
      setValidationState('ERROR');
      setValidatationErrors(errors);
    } else {
      setValidationState('SUCCESS');
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <>
      <div
        {...getRootProps()}
        style={{
          border: isDragActive ? '2px solid white' : '2px solid #4e73df',
          backgroundColor: isDragActive ? '#4e73df' : undefined,
          color: isDragActive ? 'white' : undefined,
          borderRadius: compact ? '6px' : '10px',
          cursor: 'pointer',
          padding: compact ? '0.4rem 0.75rem' : undefined,
        }}
        className="text-center"
      >
        <input {...getInputProps()} />
        {compact ? (
          <span className="small">
            <i className="fas fa-cloud-upload-alt mr-2" />
            {isDragActive ? 'Drop file here…' : 'Drop file or click to upload'}
          </span>
        ) : (
          <>
            <p>Asset Upload</p>
            {isDragActive ? <p>Drop Files Here...</p> : <p>Drop files or click here to upload</p>}
          </>
        )}
      </div>
      {selectedFile && (
        <div className={`w-100 text-center ${compact ? 'mt-2' : ''}`}>
          {!compact && <br />}
          {showSelectedFile && (
            <p className={`${compact ? 'small mb-1' : 'mb-2'}`}>
              Selected: <code>{(selectedFile as File).name}</code> ({Math.ceil((selectedFile as File).size / 1024)} KB)
            </p>
          )}
          {!!onValidate && (
            <>
              {validationState === 'INIT' && (
                <Button
                  type={buttonType}
                  size={buttonSize}
                  variant="success"
                  className="mr-4"
                  onClick={() => {
                    handleValidate();
                  }}
                >
                  <i className="fas fa-bug mr-2" />
                  Validate
                </Button>
              )}
              {validationState === 'VALIDATING' && <Loader />}
              {validationState === 'SUCCESS' && (
                <Badge pill variant="success" className="mr-4">
                  Validation Successful <i className="fas fa-check" />
                </Badge>
              )}
              {validationState === 'ERROR' && (
                <Badge className="mr-4" variant="danger" pill>
                  Errors when validating <i className="fas fa-times" />
                </Badge>
              )}
              {validationState === 'ERROR' && validationErrors.length > 0 && (
                <ul>
                  {validationErrors.map((err) => {
                    return <li>{err}</li>;
                  })}
                </ul>
              )}
            </>
          )}
          <Button
            type={buttonType}
            size={buttonSize}
            onClick={() => {
              handleUpload();
            }}
          >
            <i className="fas fa-cloud-upload-alt mr-2" />
            Upload
          </Button>
        </div>
      )}
    </>
  );
}
